import { extractSocialHandle } from './brandSocialsUtils'

// Gets a list of all advertiser ids from a brand's socials.
const getBrandAdvertiserIds = (brand) => {
  if (!brand?.id || !Array.isArray(brand.socials)) return []
  if (Array.isArray(brand.socials)) return brand.socials.map(social => social.advertiser_id).filter(Boolean)
}

const getBrandDomains = (brand) => {
  if (!Array.isArray(brand?.domains)) return []
  return brand.domains.map(domain => domain.domain).filter(Boolean)
}

// The brand's primary domain, or its first one when none is marked primary
const getBrandPrimaryDomain = (brand) => {
  const primaryDomain = (brand?.domains || []).find(domain => domain.is_primary && domain.domain)
  return primaryDomain?.domain ?? getBrandDomains(brand)[0] ?? null
}

const getBrandMetaSocials = (brand) => {
  if (!brand?.id) return []
  if (Array.isArray(brand.socials)) return brand.socials.filter(social => social.network === 'facebook')
}

const getBrandMetaPageIds = (brand) => {
  if (!brand?.id) return []
  return getBrandMetaSocials(brand).map(social => social.advertiser_id).filter(Boolean)
}

// Page id of the brand's primary Facebook page, for linking to its Ad Library
const getBrandPrimaryMetaPageId = (brand) => {
  const primarySocial = (getBrandMetaSocials(brand) || []).find(social => social.is_primary)
  return primarySocial?.advertiser_id || null
}

const BRAND_AD_DATA_STATES = {
  TRACKED: 'tracked', // We hold ads for the brand
  TRACKABLE: 'trackable', // No ads yet, but it has a Meta ad library we can scrape once it's tracked
  UNTRACKABLE: 'untrackable' // No ads, and no Meta ad library to scrape
}

// Takes a brand as returned by the /brands endpoints (with its analytics and socials)
const getBrandAdDataState = (brand) => {
  const { active_count: activeCount = 0, inactive_count: inactiveCount = 0 } = brand?.analytics || {}
  if (activeCount + inactiveCount > 0) return BRAND_AD_DATA_STATES.TRACKED
  const hasMetaAdLibrary = (brand?.socials || []).some(social => social.network === 'facebook' && social.advertiser_id)
  return hasMetaAdLibrary ? BRAND_AD_DATA_STATES.TRACKABLE : BRAND_AD_DATA_STATES.UNTRACKABLE
}

// For populating the advertiser selection dropdown
const getBrandMetaAdvertiserOptions = (brand) => {
  if (!brand?.id) return []
  return getBrandMetaSocials(brand).map(social => ({
    advertiserId: social.advertiser_id,
    name: social.name ?? extractSocialHandle(social.url),
    network: social.network,
    url: social.url,
    isPrimary: social.is_primary,
  })).filter(social => social.advertiserId)
}

const normalizeForMatching = (text) => (text || '').toLowerCase().trim().replace(/\s+/g, ' ')

// How closely a name matches an (already normalized) query: 3 exact, 2 prefix, 1 start of a word, 0 anywhere, -1 not at all
const getNameMatchRank = (name, query) => {
  const normalizedName = normalizeForMatching(name)
  if (normalizedName === query) return 3
  if (normalizedName.startsWith(query)) return 2
  if (normalizedName.includes(` ${query}`)) return 1
  if (normalizedName.includes(query)) return 0
  return -1
}

// Big brands list their subsidiaries and alternate names in brand_names (e.g. Twitch and Audible under Amazon).
// When the query matches one of those more closely than the brand's own name, that name is shown instead, with
// the brand's own name as parentName to render after it in parentheses: "Twitch (Amazon)".
// Ties go to the brand's own name, then to the shortest alternate.
const getBrandDisplayName = (name, alternateNames, query) => {
  const normalizedQuery = normalizeForMatching(query)
  if (!name || !normalizedQuery || !alternateNames?.length) return { name, parentName: null }
  let bestAlternate = null
  let bestRank = getNameMatchRank(name, normalizedQuery)
  alternateNames.forEach(alternate => {
    const rank = getNameMatchRank(alternate, normalizedQuery)
    if (rank > bestRank || (rank === bestRank && bestAlternate && alternate.length < bestAlternate.length)) {
      bestAlternate = alternate
      bestRank = rank
    }
  })
  return bestAlternate ? { name: bestAlternate, parentName: name } : { name, parentName: null }
}

// Flattens a /brands response into the single list the UI renders. Pending brands are still
// being processed by the backend, so they have no brand id, socials, or created date yet.
// A pending brand is either an ad library page ({ ref_type: 'meta_ad_library', ref: advertiser id })
// or a website ({ ref_type: 'domain', ref: domain }). Domains never have a name or avatar, and a page's
// name comes from an Ad Library lookup that can fail, so the name falls back to the ref.
const normalizeBrandsForSubscriptionsList = (subscribed = [], pending = []) => [
  ...subscribed.map(brand => ({
    key: brand.id,
    id: brand.id,
    name: brand.name,
    avatar: brand.avatar_url,
    createdAt: brand.created_at,
    dataSince: brand.scraping_data_since,
    advertiserIds: getBrandAdvertiserIds(brand),
    domains: getBrandDomains(brand),
    lastChecked: brand.last_checked_at || null,
    analytics: brand.analytics,
    pending: false,
    brand
  })),
  ...pending.map(brand => {
    const isDomain = brand.ref_type === 'domain'
    return {
      key: `${brand.ref_type}:${brand.ref}`,
      id: null,
      name: brand.name || (isDomain ? brand.ref : `Page ID ${brand.ref}`),
      avatar: brand.avatar_url,
      createdAt: null,
      dataSince: null,
      advertiserIds: brand.advertiser_id ? [brand.advertiser_id] : [],
      domains: isDomain ? [brand.ref] : [],
      lastChecked: null,
      analytics: null,
      pending: true,
      brand
    }
  })
]

export {
  getBrandAdvertiserIds,
  getBrandDomains,
  getBrandPrimaryDomain,
  getBrandMetaSocials,
  getBrandMetaPageIds,
  getBrandPrimaryMetaPageId,
  getBrandMetaAdvertiserOptions,
  BRAND_AD_DATA_STATES,
  getBrandAdDataState,
  getBrandDisplayName,
  normalizeBrandsForSubscriptionsList
}